// Лучки УЗЕ fleet: 16× Kehua PCS strip + 8× Jinko battery panel.
// Renders only for multi-PCS stations (count >= 2); single-PCS (Табаки) → null.
import i18n from '@/i18n';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { useBessLatest, type BessUnit, type BessBattery } from '@/scada/useBess';

const n = (v: number | null | undefined, d = 1) => (v == null ? '—' : Number(v).toFixed(d));
const kw = (v: number) => Math.round(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA');

const STATUS_LABEL: Record<string, string> = {
  get charging() { return i18n.t('ak_scada_charge'); }, get discharging() { return i18n.t('ak_scada_discharge'); }, get standby() { return i18n.t('ak_scada_standby_3'); }, get off() { return i18n.t('ak_scada_off'); }, get fault() { return i18n.t('ak_scada_alarm'); }, get derate() { return i18n.t('ak_scada_derated'); },
};
function statusColor(u: BessUnit) {
  if (u.alarmActive || u.status === 'fault') return scada.state.alarm;
  if (u.status === 'charging') return scada.state.curtailed;
  if (u.status === 'discharging') return scada.state.ok;
  if (u.quality !== 'ok') return scada.state.offline;
  return scada.textDim; // off / standby
}

function PcsTile({ u }: { u: BessUnit }) {
  const c = statusColor(u);
  return (
    <div style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 8, padding: '8px 9px' }}>
      <div className="flex items-center justify-between">
        <span style={{ color: scada.text, fontSize: 12, fontWeight: 700 }}>{u.pcsName}</span>
        <span className="flex items-center gap-1" style={{ color: c, fontSize: 10 }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: c, display: 'inline-block' }} />
          {STATUS_LABEL[u.status] ?? u.status}
        </span>
      </div>
      <div style={{ color: c, fontSize: 17, fontWeight: 700, marginTop: 3, lineHeight: 1.1 }}>
        {n(u.powerKw, 0)}<span style={{ fontSize: 9, color: scada.textDim, fontWeight: 400 }}> {i18n.t('ak_scada_kw')}</span>
      </div>
      <div className="flex justify-between" style={{ marginTop: 5, color: scada.textMuted, fontSize: 10 }}>
        <span>{n(u.dcVoltageV, 0)} {i18n.t('ak_scada_v')}</span>
        <span>{n(u.gridHz, 2)} {i18n.t('ak_scada_hz')}</span>
        <span>{n(u.innerTempC, 0)}°C</span>
      </div>
      <div className="flex justify-between" style={{ marginTop: 2, color: scada.textDim, fontSize: 10 }}>
        <span>SOC {n(u.socPct, 0)}%</span>
        <span>{n(u.loadRatioPct, 0)}{i18n.t('ak_scada_load')}</span>
      </div>
    </div>
  );
}

function BatteryCard({ b }: { b: BessBattery }) {
  const soc = b.socPct ?? 0;
  const socColor = soc >= 60 ? scada.state.ok : soc >= 25 ? scada.state.curtailed : scada.state.warning;
  const spreadHigh = (b.cellVspreadMv ?? 0) > 50;
  return (
    <div style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 8, padding: 10 }}>
      <div className="flex items-center justify-between">
        <span style={{ color: scada.text, fontSize: 12, fontWeight: 700 }}>{i18n.t('ak_scada_battery', { id: b.batteryId })}</span>
        <span style={{ color: scada.textDim, fontSize: 10 }}>{b.pcsUnits.join(' · ')}</span>
      </div>
      <div style={{ marginTop: 7, height: 8, background: scada.border, borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, soc))}%`, height: '100%', background: socColor }} />
      </div>
      <div className="flex items-baseline justify-between" style={{ marginTop: 4 }}>
        <span style={{ color: socColor, fontSize: 16, fontWeight: 700 }}>{n(soc, 1)}<span style={{ fontSize: 10, color: scada.textDim }}> % SOC</span></span>
        <span style={{ color: scada.textMuted, fontSize: 10 }}>SOH {n(b.sohPct, 0)}%</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1" style={{ marginTop: 7, color: scada.textMuted, fontSize: 10 }}>
        <span>{i18n.t('ak_scada_voltage_2')} <span style={{ color: scada.textDim }}>{n(b.packV, 0)} {i18n.t('ak_scada_v')}</span></span>
        <span>{i18n.t('ak_scada_temp')} <span style={{ color: scada.textDim }}>{n(b.tempMinC, 0)}–{n(b.tempMaxC, 0)}°C</span></span>
        <span>{i18n.t('ak_scada_cells')} <span style={{ color: scada.textDim }}>{n(b.cellVminMv, 0)}–{n(b.cellVmaxMv, 0)} {i18n.t('ak_scada_mv')}</span></span>
        <span>{i18n.t('ak_scada_spread')} <span style={{ color: spreadHigh ? scada.state.warning : scada.textDim }}>{n(b.cellVspreadMv, 0)} {i18n.t('ak_scada_mv')}</span></span>
      </div>
    </div>
  );
}

export default function BessFleet({ assetId }: { assetId?: string }) {
  const { data } = useBessLatest(assetId);
  if (!data || data.count < 2) return null; // тільки для станцій з кількома PCS (Лучки)
  const s = data.summary;
  const flowLabel = s.flow.startsWith('discharging') ? i18n.t('ak_scada_discharge') : s.flow.startsWith('charging') ? i18n.t('ak_scada_charge') : i18n.t('ak_scada_standby_3');
  const flowColor = s.flow.startsWith('discharging') ? scada.state.ok : s.flow.startsWith('charging') ? scada.state.curtailed : scada.textDim;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
      <SectionCard
        title={i18n.t('ak_scada_bess_converters_kehua_pcs', { n: data.count })}
        subtitle={i18n.t('ak_scada_power_dc_voltage_frequency_temperature')}
        right={<span style={{ color: flowColor, fontSize: 11 }}>{s.online}/{data.count} {i18n.t('ak_scada_online_2')} · {flowLabel} {kw(Math.abs(s.powerKw))} {i18n.t('ak_scada_kw')}{s.alarmsActive ? ` · ⚠ ${s.alarmsActive}` : ''}</span>}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {data.units.map((u) => <PcsTile key={u.unitId} u={u} />)}
        </div>
      </SectionCard>

      <SectionCard
        title={i18n.t('ak_scada_bess_batteries_jinko', { n: data.batteryCount })}
        subtitle={i18n.t('ak_scada_soc_soh_pack_voltage_cell_balance_temp')}
        right={<span style={{ color: scada.textMuted, fontSize: 11 }}>SOH {n(s.sohPct, 0)}% · {i18n.t('ak_scada_available')} ↑{kw(s.chargeAvailableKwh)} / ↓{kw(s.dischargeAvailableKwh)} {i18n.t('ak_scada_kwh')}</span>}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.batteries.map((b) => <BatteryCard key={b.batteryId} b={b} />)}
        </div>
      </SectionCard>
    </div>
  );
}
